/**
 * Premium Build Overview.
 *
 * Scoped custom element. On entering the viewport it reveals the spec strip
 * and counts integer values up from zero.
 */
if (!customElements.get('premium-build-overview')) {
  customElements.define(
    'premium-build-overview',
    class PremiumBuildOverview extends HTMLElement {
      connectedCallback() {
        this.counters = [...this.querySelectorAll('[data-count-to]')];
        this.countEnabled = this.dataset.countUp === 'true';
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.revealed = false;
        this.rail = this.querySelector('.pbo__strip-row');
        this.railWrap = this.querySelector('.pbo__strip');

        this.classList.add('is-observing');

        if (this.countEnabled && !this.reduceMotion) {
          this.counters.forEach((el) => {
            el.textContent = `0${el.dataset.countSuffix || ''}`;
          });
        }

        this.bindRail();

        if (this.reduceMotion || !('IntersectionObserver' in window)) {
          this.reveal();
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.reveal();
            });
          },
          { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        this.unbindRail();
      }

      bindRail() {
        if (!this.rail || !this.railWrap) return;

        this.updateRail = this.updateRail.bind(this);
        this.rail.addEventListener('scroll', this.updateRail, { passive: true });
        this.railMedia = window.matchMedia('(max-width: 749px)');
        this.railMedia.addEventListener('change', this.updateRail);

        if (typeof ResizeObserver !== 'undefined') {
          this.railObserver = new ResizeObserver(this.updateRail);
          this.railObserver.observe(this.rail);
        }

        this.updateRail();
      }

      unbindRail() {
        if (!this.rail || !this.updateRail) return;

        this.rail.removeEventListener('scroll', this.updateRail);
        if (this.railMedia) this.railMedia.removeEventListener('change', this.updateRail);
        if (this.railObserver) this.railObserver.disconnect();
      }

      updateRail() {
        const { rail, railWrap } = this;
        if (!rail || !railWrap) return;

        const mobile = window.matchMedia('(max-width: 749px)').matches;
        if (!mobile) {
          railWrap.classList.remove('is-scrollable', 'is-at-start', 'is-at-end');
          return;
        }

        const scrollable = rail.scrollWidth > rail.clientWidth + 1;
        railWrap.classList.toggle('is-scrollable', scrollable);
        railWrap.classList.toggle('is-at-start', rail.scrollLeft <= 1);
        railWrap.classList.toggle('is-at-end', rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1);
      }

      reveal() {
        if (this.revealed) return;
        this.revealed = true;
        if (this.observer) this.observer.disconnect();

        this.classList.add('is-visible');

        if (this.countEnabled && !this.reduceMotion) {
          this.counters.forEach((el) => {
            window.setTimeout(() => this.countUp(el), 180);
          });
        }
      }

      countUp(el) {
        const target = parseInt(el.dataset.countTo, 10);
        if (Number.isNaN(target)) return;

        const suffix = el.dataset.countSuffix || '';
        const duration = 1400;
        const start = performance.now();
        const easeOut = (t) => 1 - Math.pow(1 - t, 3);

        const tick = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          el.textContent = `${Math.round(easeOut(progress) * target)}${suffix}`;
          if (progress < 1) requestAnimationFrame(tick);
        };

        requestAnimationFrame(tick);
      }
    }
  );
}
